import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BRAND } from "@/constants/content";
import { LogOut, CalendarCheck, Inbox, Briefcase, Clock3, Phone, MessageSquare, Trash2, RefreshCw, Download, Search } from "lucide-react";

const BOOKING_STATUSES = ["new", "contacted", "confirmed", "in_progress", "completed", "cancelled"];
const CAREER_STATUSES = ["new", "shortlisted", "interviewed", "hired", "rejected"];
const CONTACT_STATUSES = ["new", "responded", "closed"];

const statusColor = (s) => ({
  new: "bg-blue-100 text-blue-700 border-blue-200",
  contacted: "bg-amber-100 text-amber-700 border-amber-200",
  confirmed: "bg-teal-100 text-teal-700 border-teal-200",
  in_progress: "bg-indigo-100 text-indigo-700 border-indigo-200",
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  shortlisted: "bg-amber-100 text-amber-700 border-amber-200",
  interviewed: "bg-indigo-100 text-indigo-700 border-indigo-200",
  hired: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-slate-100 text-slate-600 border-slate-200",
  responded: "bg-teal-100 text-teal-700 border-teal-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
}[s] || "bg-slate-100 text-slate-700 border-slate-200");

const urgencyColor = (u) => ({
  standard: "bg-slate-50 text-slate-600 border-slate-200",
  urgent: "bg-amber-50 text-amber-700 border-amber-200",
  emergency: "bg-red-50 text-red-700 border-red-200",
}[u] || "bg-slate-50 text-slate-600");

export default function AdminDashboard() {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [careers, setCareers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [bkSearch, setBkSearch] = useState("");
  const [bkStatus, setBkStatus] = useState("all");
  const [crSearch, setCrSearch] = useState("");
  const [crStatus, setCrStatus] = useState("all");
  const [ctSearch, setCtSearch] = useState("");
  const [ctStatus, setCtStatus] = useState("all");

  const logout = () => {
    localStorage.removeItem("kinflex_admin_token");
    localStorage.removeItem("kinflex_admin_user");
    nav("/admin/login");
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, ct, s] = await Promise.all([
        api.get("/bookings"),
        api.get("/careers"),
        api.get("/contacts"),
        api.get("/admin/summary"),
      ]);
      setBookings(b.data);
      setCareers(c.data);
      setContacts(ct.data);
      setSummary(s.data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("kinflex_admin_token")) { nav("/admin/login"); return; }
    fetchAll();
  }, [fetchAll, nav]);

  const updateStatus = async (endpoint, id, status, listSetter) => {
    try {
      await api.patch(`/${endpoint}/${id}`, { status });
      listSetter((p) => p.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Status updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (endpoint, id, listSetter, label) => {
    if (!window.confirm(`Delete this ${label}?`)) return;
    try {
      await api.delete(`/${endpoint}/${id}`);
      listSetter((p) => p.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadCsv = (endpoint, filename) => {
    const token = localStorage.getItem("kinflex_admin_token");
    fetch(`${API}/${endpoint}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export failed"));
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const filterList = (list, search, status, fields) => list.filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return fields.some((f) => (r[f] || "").toString().toLowerCase().includes(q));
  });

  const filteredBookings = useMemo(() => filterList(bookings, bkSearch, bkStatus, ["full_name", "phone", "email", "service_type", "city"]), [bookings, bkSearch, bkStatus]);
  const filteredCareers = useMemo(() => filterList(careers, crSearch, crStatus, ["name", "phone", "email", "role", "city"]), [careers, crSearch, crStatus]);
  const filteredContacts = useMemo(() => filterList(contacts, ctSearch, ctStatus, ["name", "phone", "email", "subject", "message"]), [contacts, ctSearch, ctStatus]);

  const renderToolbar = (search, setSearch, status, setStatus, statuses, exportEndpoint, exportName, placeholder) => (
    <div className="flex flex-col md:flex-row gap-2 md:items-center justify-between mb-4">
      <div className="flex flex-1 gap-2 max-w-lg">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={placeholder} className="pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" size="sm" onClick={() => downloadCsv(exportEndpoint, exportName)} className="rounded-full">
        <Download className="h-4 w-4 mr-1.5" /> Export CSV
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="KinFlex" className="h-9 w-9 rounded-lg object-cover ring-1 ring-slate-200" />
            <div className="leading-tight">
              <div className="font-display font-bold text-slate-900 text-sm">KinFlex Admin</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-teal-600 font-semibold">Dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchAll} data-testid="admin-refresh-btn" className="rounded-full">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-btn" className="rounded-full text-red-600 hover:text-red-700">
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display font-bold text-slate-900 text-2xl md:text-3xl">Leads &amp; Applications</h1>
        <p className="text-slate-500 text-sm mt-1">Manage appointment leads, career applications and contact messages.</p>

        {summary && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Appointment Leads", value: summary.bookings.total, icon: CalendarCheck, color: "from-blue-600 to-blue-900" },
              { label: "New Bookings", value: summary.bookings.new, icon: Inbox, color: "from-teal-500 to-teal-700" },
              { label: "Career Applications", value: summary.careers?.total ?? 0, icon: Briefcase, color: "from-indigo-500 to-indigo-700" },
              { label: "Contact Messages", value: summary.contacts.total, icon: MessageSquare, color: "from-rose-500 to-rose-700" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-500 uppercase tracking-[0.14em] font-semibold">{s.label}</div>
                  <div className={`h-9 w-9 rounded-lg bg-gradient-to-br ${s.color} text-white flex items-center justify-center`}>
                    <s.icon className="h-4 w-4" />
                  </div>
                </div>
                <div className="mt-3 font-display font-bold text-3xl text-slate-900">{s.value}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8">
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700 mb-3">Leads</div>
          <Tabs defaultValue="appointments">
            <TabsList className="bg-white border border-slate-200 rounded-full p-1" data-testid="admin-tabs">
              <TabsTrigger value="appointments" className="rounded-full px-5 data-[state=active]:bg-blue-900 data-[state=active]:text-white" data-testid="tab-appointments">
                <CalendarCheck className="h-4 w-4 mr-1.5" /> Appointment Leads ({bookings.length})
              </TabsTrigger>
              <TabsTrigger value="careers" className="rounded-full px-5 data-[state=active]:bg-blue-900 data-[state=active]:text-white" data-testid="tab-careers">
                <Briefcase className="h-4 w-4 mr-1.5" /> Career Applications ({careers.length})
              </TabsTrigger>
              <TabsTrigger value="contacts" className="rounded-full px-5 data-[state=active]:bg-blue-900 data-[state=active]:text-white" data-testid="tab-contacts">
                <MessageSquare className="h-4 w-4 mr-1.5" /> Contact Messages ({contacts.length})
              </TabsTrigger>
            </TabsList>

            {/* Appointment Leads */}
            <TabsContent value="appointments" className="mt-5">
              {renderToolbar(bkSearch, setBkSearch, bkStatus, setBkStatus,
                BOOKING_STATUSES, "bookings/export.csv", "appointment_leads.csv",
                "Search name, phone, email, service, city...")}
              <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="bookings-table">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-3">Patient</th>
                        <th className="text-left px-4 py-3">Service</th>
                        <th className="text-left px-4 py-3">City / Address</th>
                        <th className="text-left px-4 py-3">Urgency</th>
                        <th className="text-left px-4 py-3">Created</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-right px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={7} className="text-center py-10 text-slate-400">Loading...</td></tr>
                      ) : filteredBookings.length === 0 ? (
                        <tr><td colSpan={7} className="text-center py-10 text-slate-400">No bookings match your filters.</td></tr>
                      ) : filteredBookings.map((b) => (
                        <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`booking-row-${b.id}`}>
                          <td className="px-4 py-3 align-top">
                            <div className="font-semibold text-slate-900">{b.full_name}</div>
                            <div className="text-xs text-slate-500 flex flex-col gap-0.5 mt-0.5">
                              <a href={`tel:${b.phone}`} className="hover:text-teal-700 inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {b.phone}</a>
                              {b.email && <span className="text-slate-400">{b.email}</span>}
                              {b.patient_age && <span className="text-slate-400">Age: {b.patient_age}</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 align-top text-slate-700">
                            <div>{b.service_type}</div>
                            {b.preferred_datetime && <div className="text-xs text-slate-500 mt-0.5">{b.preferred_datetime}</div>}
                            {b.medical_condition && <div className="text-xs text-slate-500 mt-0.5 max-w-[220px] truncate" title={b.medical_condition}>Cond: {b.medical_condition}</div>}
                          </td>
                          <td className="px-4 py-3 align-top text-slate-700">
                            <div>{b.city}</div>
                            {b.address && <div className="text-xs text-slate-500 mt-0.5 max-w-[220px] truncate" title={b.address}>{b.address}</div>}
                          </td>
                          <td className="px-4 py-3 align-top"><Badge variant="outline" className={`${urgencyColor(b.urgency)} font-medium capitalize`}>{b.urgency}</Badge></td>
                          <td className="px-4 py-3 align-top text-slate-500 text-xs whitespace-nowrap">{fmtDate(b.created_at)}</td>
                          <td className="px-4 py-3 align-top">
                            <Select value={b.status} onValueChange={(v) => updateStatus("bookings", b.id, v, setBookings)}>
                              <SelectTrigger className={`h-8 px-2.5 text-xs font-medium border ${statusColor(b.status)} w-[140px]`} data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-right align-top">
                            <button onClick={() => remove("bookings", b.id, setBookings, "booking")} className="text-slate-400 hover:text-red-600 p-1.5" data-testid={`booking-delete-${b.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Career Applications */}
            <TabsContent value="careers" className="mt-5">
              {renderToolbar(crSearch, setCrSearch, crStatus, setCrStatus,
                CAREER_STATUSES, "careers/export.csv", "career_applications.csv",
                "Search name, phone, email, role, city...")}
              <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="careers-table">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-3">Applicant</th>
                        <th className="text-left px-4 py-3">Role</th>
                        <th className="text-left px-4 py-3">Qualification / Exp</th>
                        <th className="text-left px-4 py-3">City</th>
                        <th className="text-left px-4 py-3">Resume</th>
                        <th className="text-left px-4 py-3">Created</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-right px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={8} className="text-center py-10 text-slate-400">Loading...</td></tr>
                      ) : filteredCareers.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-10 text-slate-400">No applications match your filters.</td></tr>
                      ) : filteredCareers.map((c) => (
                        <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`career-row-${c.id}`}>
                          <td className="px-4 py-3 align-top">
                            <div className="font-semibold text-slate-900">{c.name}</div>
                            <div className="text-xs text-slate-500 flex flex-col gap-0.5 mt-0.5">
                              <a href={`tel:${c.phone}`} className="hover:text-teal-700 inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</a>
                              {c.email && <span className="text-slate-400">{c.email}</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 align-top text-slate-700">{c.role}</td>
                          <td className="px-4 py-3 align-top text-slate-700 text-xs">
                            {c.qualification && <div>{c.qualification}</div>}
                            {c.experience && <div className="text-slate-500 mt-0.5">Exp: {c.experience}</div>}
                          </td>
                          <td className="px-4 py-3 align-top text-slate-700">{c.city || "—"}</td>
                          <td className="px-4 py-3 align-top">
                            {c.resume_url ? <a href={c.resume_url} target="_blank" rel="noreferrer" className="text-xs text-teal-700 underline">View</a> : <span className="text-slate-400 text-xs">—</span>}
                          </td>
                          <td className="px-4 py-3 align-top text-slate-500 text-xs whitespace-nowrap">{fmtDate(c.created_at)}</td>
                          <td className="px-4 py-3 align-top">
                            <Select value={c.status} onValueChange={(v) => updateStatus("careers", c.id, v, setCareers)}>
                              <SelectTrigger className={`h-8 px-2.5 text-xs font-medium border ${statusColor(c.status)} w-[140px]`} data-testid={`career-status-${c.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{CAREER_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-right align-top">
                            <button onClick={() => remove("careers", c.id, setCareers, "application")} className="text-slate-400 hover:text-red-600 p-1.5" data-testid={`career-delete-${c.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Contact Messages */}
            <TabsContent value="contacts" className="mt-5">
              {renderToolbar(ctSearch, setCtSearch, ctStatus, setCtStatus,
                CONTACT_STATUSES, "contacts/export.csv", "contact_messages.csv",
                "Search name, phone, email, subject...")}
              <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="contacts-table">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-3">Name</th>
                        <th className="text-left px-4 py-3">Subject / Message</th>
                        <th className="text-left px-4 py-3">Created</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-right px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={5} className="text-center py-10 text-slate-400">Loading...</td></tr>
                      ) : filteredContacts.length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-10 text-slate-400">No messages match your filters.</td></tr>
                      ) : filteredContacts.map((c) => (
                        <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`contact-row-${c.id}`}>
                          <td className="px-4 py-3 align-top">
                            <div className="font-semibold text-slate-900">{c.name}</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              <a href={`tel:${c.phone}`} className="hover:text-teal-700">{c.phone}</a>
                              {c.email && <div>{c.email}</div>}
                            </div>
                          </td>
                          <td className="px-4 py-3 align-top max-w-md">
                            {c.subject && <div className="font-medium text-slate-800">{c.subject}</div>}
                            <div className="text-sm text-slate-600 line-clamp-2">{c.message}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap align-top">{fmtDate(c.created_at)}</td>
                          <td className="px-4 py-3 align-top">
                            <Select value={c.status} onValueChange={(v) => updateStatus("contacts", c.id, v, setContacts)}>
                              <SelectTrigger className={`h-8 px-2.5 text-xs font-medium border ${statusColor(c.status)} w-[120px]`} data-testid={`contact-status-${c.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{CONTACT_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-right align-top">
                            <button onClick={() => remove("contacts", c.id, setContacts, "message")} className="text-slate-400 hover:text-red-600 p-1.5" data-testid={`contact-delete-${c.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
